// Carriers whose coverage is owned by the marketplace or the shipper itself.
// Cubbo cannot edit those coverages, so the UI surfaces a read-only state
// rather than a "manage coverages" affordance.
// TODO: replace with a backend flag on ShippingMethod when available.
export const EXTERNAL_CARRIER_NAMES = [
    'Externo',
    'Almacén',
    'Armazém',
    'Mercado Envíos',
    'AMAZON',
]

export const isExternalShippingMethod = (method) =>
    EXTERNAL_CARRIER_NAMES.includes(method?.carrier_name)

// Blue Express is the Chilean carrier whose emission Cubbo itself makes, so the
// cash-on-delivery instruction can travel inside that request. Pickup is excluded because
// that delivery type detours around the emission entirely.
export const CARRIER_NAME_BLUEX = 'Blue Express'
// `delivery_type`, capital P — NOT `shipping_type`, whose pickup value is the lowercase
// 'pickup' (SHIPPING_TYPES.PICKUP, in the GenerateShipment constants). Both are correct and
// both are read by the same function a few lines apart, off DIFFERENT columns: platform defines
// SHIPPING_TYPE_PICKUP = 'pickup' and DELIVERY_TYPE_PICKUP = 'Pickup'. That is what makes the
// pair a trap rather than a bug — nothing looks wrong at either call site. The mirror below needs
// this one, because delivery_type is the column blue_express_service branches on when it takes
// the pickup path. Reaching for the already-imported neighbour, or "fixing" the capitalisation to
// match it, makes supportsCod answer true for a Blue Express PICKUP: the browser then permits a
// collect instruction on a delivery where no courier ever shows up.
export const DELIVERY_TYPE_PICKUP = 'Pickup'

// Mirrors ShippingMethod#supports_cod? on the backend, which is the enforcing side. Two ways
// a method can collect: a dedicated COD method (the Mexico/Colombia twins, is_cod: true), or
// a carrier that takes the instruction inside the emission Cubbo makes.
//
// Returns null when there is no method to judge yet, so callers can tell "cannot say" apart
// from "no" and avoid blocking before a method has been chosen. Callers must therefore
// compare against `false` explicitly, never rely on falsiness.
export const supportsCod = (shippingMethod) => {
    if (!shippingMethod) return null
    if (shippingMethod.is_cod === true) return true
    // internal_carrier_name first. ShippingMethodSerializer OVERRIDES carrier_name with the
    // whitelabel display name (a UBER method renders as "Cubbo"), so the backend predicate
    // compares the database column while this mirror was comparing the brand — the two halves of
    // "one rule in two dialects" reading different fields.
    //
    // It cannot fail today: UBER is the only whitelabeled carrier, and a UBER method that
    // collects carries is_cod: true, which short-circuits above before any carrier is read. It
    // fails the day a carrier that qualifies by IDENTITY rather than by the is_cod flag is sold
    // under the Cubbo brand — supportsCod would read "Cubbo", not recognise "Blue Express",
    // answer false, and the browser would refuse what the backend accepts. That is the BEX-3
    // defect again, one field over, and invisible to the backend specs for the same reason.
    //
    // Also the house rule for this surface: CLAUDE.md asks carrier/dispatch and
    // shipment-generation surfaces to show the real carrier, and both neighbours on this very
    // screen already comply (MultiBoxPackingPanel, ShippingMethodSelect — CUBBO-2185). Read
    // inline rather than through realCarrierName, which would pull React into a plain utils
    // module. Both feeds carry the field: ShippingMethodSerializer sets
    // expose_internal_carrier_name? to true, and GET /warehouses/:id/shipping-methods renders
    // with that serializer.
    const carrier = shippingMethod.internal_carrier_name || shippingMethod.carrier_name
    if (!carrier) return null

    return carrier === CARRIER_NAME_BLUEX
        && shippingMethod.delivery_type !== DELIVERY_TYPE_PICKUP
}

// The DECISION, not just the capability — so the thing that actually regressed is testable. The
// BEX-3 defect was never in a predicate: it was in the call site, which asked
// `data?.is_cod && !shippingMethod?.is_cod` — identity where capability was meant. A suite that
// stops at the helper boundary stays green against a wrong gate, so the gate itself moves here,
// following this screen's own pattern (six test files under GenerateShipment/__tests__, each
// covering a pure module extracted out of the component, none of them rendering it).
//
// `!== true`, not `=== false`: supportsCod answers null when there is no method to judge, and for
// a gate whose enforcing side lives in another repo, "cannot say" must not read as "permitted".
// On this screen a method is always present by the time generation is attempted, so the
// difference is theoretical — which is exactly when the safe default is free.
export const codBlocksLabelGeneration = (order, shippingMethod) =>
    Boolean(order?.is_cod) && supportsCod(shippingMethod) !== true
